/**
 * What a shopper may be shown of a product whose ASIN a judge said is another
 * product in this market (`amazonMatch: 'wrong'`, from migration 0056 through
 * applyCanonicalCache). This is the one place the wrong Amazon item
 * disappears; storage keeps the ASIN, which anchors the product's offers.
 *
 * Why (2026-09-21): about four Amazon links in ten were another product (an
 * accessory, another model, a renewed unit). Olivier: when we cannot find the
 * right product, there is always an Amazon search.
 *
 * For such a product it returns a copy in which:
 *  - an Amazon product link becomes the Amazon search for the product's own
 *    name, partner tag kept, with none of the listing's images;
 *  - the ASIN is gone, so no /dp/ link, reviews link, kit checkout or JSON-LD
 *    identifier can be built from it;
 *  - the price is gone when it was Amazon's (the link was Amazon's);
 *  - the listing's stars (ratingSource 'amazon-asin') no longer render;
 *  - product images served from Amazon's image hosts are gone;
 *  - no Amazon row is left among its offers.
 *
 * The same happens to an Amazon product link on another market's marketplace
 * (amazon.com on an en-CA page), judged or not (2026-09-25).
 *
 * The brand shown on such a card is one the product's own name carries: a
 * brandName that the rejected listing brought ("Otto Tech", an accessory
 * seller, under Phonak Audeo Lumity) is dropped, and nothing is guessed in
 * its place.
 *
 * A shop link (not Amazon) that may not be a buy button on this page
 * (buyLinkProblem: not a product page, a store outside the market, or a page
 * naming another product) becomes the same Amazon search, without its price,
 * and so do none of its offers rows. Links that stay lose third-party
 * affiliate and ad parameters (stripThirdPartyParams). This runs at render,
 * so links stored before 2026-09-25 are covered too.
 *
 * And a product whose buy link is a shop, with no price of its own, shows the
 * price the offers engine read on that very page (withOwnLinkPrice, 2026-09-22:
 * four shop links in five on live pages had no price).
 *
 * Since 2026-09-28 (`trust-buy-buttons-open-wrong-destination` and the card
 * identity guard):
 *  - the maker's own site is the buy link only with evidence that it sells in
 *    this market (a price in the market's currency read on that site, or a
 *    reviewed merchant of the market). Without it the card gets the Amazon
 *    search, and the maker's product page stays as an information row
 *    ("Official site", never counted, never the pick) unless its URL names
 *    another market. A brand sold only on its own site keeps its store link;
 *  - an Amazon listing whose stored title (`externalLinks.alt`) is evidence of
 *    another product (card-identity.ts: another brand, another model of the
 *    card's line; never the mere absence of the card's words) is treated as a
 *    wrong Amazon item (its photos go with it; a card left with none is
 *    `imageWithheld`, as below), and a shop link whose stored title does so
 *    becomes the Amazon search. The page's own product (`searchedProduct`: a
 *    product page, the product a product search asked for) loses its listing
 *    only to another brand;
 *  - a photo that is evidence of another product (its caption, source page or
 *    address; on our own CDN its file name) is not shown. A card left with no
 *    photo carries `imageWithheld`, so the image slot renders a neutral tile
 *    and never asks for a replacement;
 *  - a price shows only with its source (Olivier, decision 2): beside a shop
 *    link, only a price read on that very page (`price.source`, recorded by
 *    the writers since 2026-09-28) or the offers engine's row for that shop
 *    (withOwnLinkPrice). A shop link's price with no source, a price read on
 *    another page, and any price beside an Amazon search are not shown (QA
 *    2026-09-25/28: "Our pick $1,590" on an oticon.ca page that shows no
 *    price). An Amazon listing keeps its price unless its source says it was
 *    read elsewhere: prices written before sources existed came from that
 *    listing (withSourcedLinkPrice).
 *
 * Since 2026-10-03, Amazon's own content follows Amazon's licence: an Amazon
 * listing shows the price of its current Creators row (read through the API
 * within 24 hours), or none, and an Amazon star rating shows only while its
 * read time is within those 24 hours (withCurrentAmazonPrice,
 * withCurrentAmazonRating).
 *
 * Every other product comes back as is, the same object.
 *
 * The card identity guard is injected (IdentityJudge), so this module never
 * imports it: the server runs the pipeline with card-identity.ts
 * (present-product.ts, which stamps what the guard refused), the browser with
 * those stamps (present-live-product.ts). The guard and its brand lists stay
 * out of the client bundle (review, 2026-09-28: 36 KB gzip on every card).
 *
 * Pure and client-safe. Never throws.
 */

import { isDtcStoreUrl } from "../data/purchase-channels";
import { getAmazonDomainForRegion } from "../types/amazon.type";
import type { ExternalLinks, ImageType, Product } from "../types/product.type";
import { getRegionPartnerTag, REGION_FOR_CODE, type RegionCode } from "../types/region.type";
import {
  buyLinkProblem,
  isMakerLink,
  isOffMarketAmazonLink,
  linkNamesProduct,
  linkSellsInMarket,
  offerStoreEvidence,
  stripThirdPartyParams,
  urlNamesOtherMarket,
} from "./buy-link-market";
import { getCurrencyForRegion } from "./currency";
import { normalizeMerchantDomain } from "./offers/merchant-domain";
import { lookupOfferMerchant, resolveOfferConfidence } from "./offers/rank-offers";
import { dtcStoreFor } from "./purchase-channel";
import { getDisplayDomain, getRetailerName } from "./retailer-name";

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * The card identity guard, as the pipeline asks it: is the stored listing
 * behind the card's link another product, is this photo another product's?
 * card-identity.ts answers on the server; the stamps it leaves answer in the
 * browser (present-live-product.ts).
 */
export interface IdentityJudge {
  listingIsAnotherProduct(product: Product, options: { brandOnly: boolean }): boolean;
  imageIsAnotherProduct(image: ImageType, product: Product): boolean;
}

/**
 * The page a link opens, as a refusal stamp names it (`refusedListings`):
 * its host and ASIN for an Amazon item, else its host and path (see pageKey).
 */
export function listingKey(url: string | null | undefined): string | null {
  return url ? pageKey(url) : null;
}

/** amazon.ca, www.amazon.co.uk, smile.amazon.com, amzn.to, a.co. */
export function isAmazonHost(host: string | null | undefined): boolean {
  if (!host) return false;
  const h = host.toLowerCase().replace(/^www\./, "");
  return /(^|\.)amazon\.[a-z.]+$/.test(h) || h === "amzn.to" || h === "a.co" || h === "amzn.eu";
}

/** m.media-amazon.com, images-na.ssl-images-amazon.com and the like. */
function isAmazonImageHost(host: string | null): boolean {
  return host !== null && (host === "m.media-amazon.com" || /(^|\.)(ssl-)?images-amazon\.com$/.test(host) || host.endsWith(".ssl-images-amazon.com"));
}

/** The Amazon search for the product's own name in this market, or null when the market is unknown. */
function amazonSearchHref(title: string, regionCode: string | null | undefined, fallbackHost: string | null): string | null {
  const term = title.trim();
  if (!term) return null;
  const code = (regionCode ?? "").toUpperCase() as RegionCode;
  const region = REGION_FOR_CODE[code];
  if (region) {
    return `https://www.${getAmazonDomainForRegion(code)}/s?k=${encodeURIComponent(term)}&tag=${getRegionPartnerTag(region)}`;
  }
  return fallbackHost ? `https://${fallbackHost}/s?k=${encodeURIComponent(term)}` : null;
}

/**
 * The presentation pipeline (see the header), with the identity guard given.
 * `now` (the server's clock) holds Amazon's prices and ratings to the
 * licence's 24 hours (withCurrentAmazonPrice, withCurrentAmazonRating).
 */
export function presentProductWith<T extends Product>(
  product: T,
  regionCode: string | null | undefined,
  judge: IdentityJudge,
  now?: number,
): T {
  if (!product) return product;
  const judgedWrong =
    product.amazonMatch === "wrong" || isOffMarketAmazonLink(product.externalLinks?.href, regionCode);
  const listingWrong =
    !judgedWrong && isAmazonItemLink(product.externalLinks) && judge.listingIsAnotherProduct(product, ownPage(product));
  const shown = judgedWrong || listingWrong ? withoutWrongAmazon(product, regionCode) : product;
  const out = withCardImages(
    withCurrentAmazonRating(
      withOwnLinkPrice(
        withCurrentAmazonPrice(withSourcedLinkPrice(withFitShopLinks(shown, regionCode, judge)), regionCode, now),
        regionCode,
      ),
      now,
    ),
    judge,
  );
  // The listing's photos were the card's only ones: a neutral tile, not a
  // paid lookup for a replacement on every visit (the identity guard's rule).
  if (listingWrong && !out.imageWithheld && hasPhoto(product) && !hasPhoto(out)) {
    return { ...out, imageWithheld: true } as T;
  }
  return out;
}

/**
 * The page's own product: a product page's, or the product a product search
 * asked for (`searchedProduct`, stamped at render by the page). Its stored
 * listing was found for it by name, so only another brand takes it away.
 */
function ownPage(product: Product): { brandOnly: boolean } {
  return { brandOnly: product.searchedProduct === true };
}

/**
 * Marks the page's own product (see ownPage): a READ-TIME overlay, never
 * persisted (EPHEMERAL_PRODUCT_KEYS). Returns the same object when already
 * marked.
 */
export function markSearchedProduct<T extends Product>(product: T): T {
  if (!product || product.searchedProduct === true) return product;
  return { ...product, searchedProduct: true };
}

/** A page's lineup with its own product marked: a product search's first product. */
export function markPageProduct<T extends Product>(products: T[], searchType: string | null | undefined): T[] {
  if (searchType !== "product" || !Array.isArray(products) || !products[0]) return products;
  const first = markSearchedProduct(products[0]);
  return first === products[0] ? products : [first, ...products.slice(1)];
}

function hasPhoto(product: Product): boolean {
  return [...(product.images ?? []), ...(product.externalLinks?.images ?? [])].some((image) => !!image?.src);
}

/** The market's currency ("CAD"), or null for an unknown market. */
function marketCurrency(regionCode: string | null | undefined): string | null {
  const region = REGION_FOR_CODE[(regionCode ?? "").toUpperCase() as RegionCode];
  return region ? getCurrencyForRegion(region) : null;
}

function isAmazonItemLink(link: ExternalLinks | undefined): boolean {
  return !!link && link.type !== "amazon-search" && (link.type === "amazon" || isAmazonHost(hostOf(link.href)));
}

/**
 * The shop link and offers rows a shopper may be sent to on this page (see
 * the header). Returns the product itself when nothing changes.
 */
export function withFitShopLinks<T extends Product>(
  product: T,
  regionCode: string | null | undefined,
  judge: IdentityJudge,
): T {
  try {
    let out: T | null = null;
    const edit = (): T => (out ??= { ...product } as T);
    const currency = marketCurrency(regionCode);
    // The maker's product page, kept as an information row when it may not be
    // the buy link (see the header).
    let officialSite: NonNullable<Product["offers"]>[number] | null = null;

    const link = product.externalLinks as ExternalLinks | undefined;
    const isShopLink = !!link?.href && link.type !== "amazon-search" && !isAmazonItemLink(link);
    // A brand sold only on its own site: the registry's store is its buy link,
    // its home page included (frame.work).
    const dtcStore = isShopLink && !!dtcStoreFor(product) && isDtcStoreUrl(link?.href);
    if (link && isShopLink && !dtcStore) {
      const sells = linkSellsInMarket(link.href, { regionCode, offers: product.offers, marketCurrency: currency, product });
      const problem =
        buyLinkProblem(link.href, { regionCode, product, evidence: { pricedInMarket: sells } }) ??
        (judge.listingIsAnotherProduct(product, ownPage(product)) ? "other-product" : null);
      const maker = isMakerLink(link.href, product);
      if (problem || (maker && !sells)) {
        const href = amazonSearchHref(product.title ?? "", regionCode, null) ?? link.amazonSearchFallback;
        if (href) {
          edit().externalLinks = { href, alt: product.title ?? link.alt ?? "", type: "amazon-search" };
          // The price was that page's.
          delete (edit() as { price?: unknown }).price;
          if (
            maker &&
            (problem === null || problem === "other-market") &&
            !urlNamesOtherMarket(link.href, regionCode) &&
            linkNamesProduct(link.href, product)
          ) {
            const url = stripThirdPartyParams(link.href);
            officialSite = {
              merchantDomain: normalizeMerchantDomain(hostOf(url) ?? ""),
              ...(product.brandName?.trim() ? { merchantName: product.brandName.trim() } : {}),
              merchantKind: "brand",
              url,
              kind: "info",
              // Provenance: the product's own link record, not an offers writer.
              source: "find-product-candidate",
            };
          }
        }
      } else {
        const clean = stripThirdPartyParams(link.href);
        if (clean !== link.href) edit().externalLinks = { ...link, href: clean };
      }
    } else if (link && dtcStore) {
      const clean = stripThirdPartyParams(link.href);
      if (clean !== link.href) edit().externalLinks = { ...link, href: clean };
    }

    if (Array.isArray(product.offers)) {
      let changed = false;
      const kept: NonNullable<Product["offers"]> = [];
      for (const offer of product.offers) {
        const url = offer?.url;
        const evidence = offerStoreEvidence(offer, currency);
        // Judged as the panel judges them (prepareOffers): product page and
        // market, not the slug-names-the-product heuristic. On offers rows,
        // which the offers engine has already matched to the product, that
        // heuristic was wrong 73% of the time in a sample of 953 live pages
        // (2026-09-25): Sephora, Ulta, PetSmart and Home Depot slugs omit the
        // brand, so right-product rows were dropped.
        if (typeof url === "string" && buyLinkProblem(url, { regionCode, info: offer.kind === "info", evidence })) {
          changed = true;
          continue;
        }
        // A row with a tracking token keeps its URL: the token is bound to it (link-token.ts).
        const clean = typeof url === "string" && !offer.trackingToken ? stripThirdPartyParams(url) : url;
        if (clean !== url) {
          changed = true;
          kept.push({ ...offer, url: clean as string });
        } else {
          kept.push(offer);
        }
      }
      if (changed) {
        if (kept.length > 0) edit().offers = kept;
        else delete (edit() as { offers?: unknown }).offers;
      }
    }

    if (officialSite) {
      const site = officialSite;
      const current = ((out ?? product) as Product).offers ?? [];
      const listed = current.some(
        (offer) => normalizeMerchantDomain(offer?.merchantDomain ?? "") === site.merchantDomain,
      );
      if (!listed) edit().offers = [...current, site];
    }
    return out ?? product;
  } catch {
    return product;
  }
}

/**
 * The card's photos, less those whose own description names another product
 * (card-identity.ts). A card left with none is marked `imageWithheld`: the
 * image slot shows a neutral tile and never fetches a replacement, which
 * would spend on every visit and write over the stored photo.
 */
export function withCardImages<T extends Product>(product: T, judge: IdentityJudge): T {
  try {
    const images = Array.isArray(product.images) ? product.images : [];
    const linkImages = Array.isArray(product.externalLinks?.images) ? product.externalLinks.images : [];
    if (images.length === 0 && linkImages.length === 0) return product;
    const keptImages = images.filter((image) => !judge.imageIsAnotherProduct(image, product));
    const keptLinkImages = linkImages.filter((image) => !judge.imageIsAnotherProduct(image, product));
    if (keptImages.length === images.length && keptLinkImages.length === linkImages.length) return product;
    const out = { ...product } as T;
    if (keptImages.length !== images.length) out.images = keptImages;
    if (keptLinkImages.length !== linkImages.length) {
      out.externalLinks = { ...out.externalLinks, images: keptLinkImages };
    }
    const anyPhoto = [...keptImages, ...keptLinkImages].some((image) => !!image?.src);
    if (!anyPhoto) (out as Product).imageWithheld = true;
    return out;
  } catch {
    return product;
  }
}

/** An Amazon item's ASIN in its URL path: /dp/B0…, /gp/product/B0…, /gp/aw/d/B0…. */
const AMAZON_ASIN_PATH = /\/(?:dp|gp\/product|gp\/aw\/d|exec\/obidos\/asin|o\/asin)\/([A-Z0-9]{10})(?=[/?#]|$)/i;

function amazonAsinOf(url: string): string | null {
  try {
    const match = AMAZON_ASIN_PATH.exec(new URL(url).pathname);
    return match ? match[1].toUpperCase() : null;
  } catch {
    return null;
  }
}

/** An Amazon search results page (/s?k=…): a place no price is ever read. */
function isAmazonSearchHref(url: string): boolean {
  try {
    const parsed = new URL(url);
    return isAmazonHost(parsed.hostname) && (/^\/s(\/|$)/.test(parsed.pathname) || parsed.searchParams.has("k"));
  } catch {
    return false;
  }
}

/**
 * The page a URL opens, to compare a price's source with a link: the host
 * (without www) and the path (without its query, which trackers and our own
 * parameter stripping change), or for an Amazon item its marketplace and ASIN
 * (amazon.com and amazon.ca are two prices).
 */
function pageKey(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const asin = isAmazonHost(host) ? amazonAsinOf(url) : null;
    if (asin) return `${host}|${asin}`;
    let path = parsed.pathname;
    try {
      path = decodeURIComponent(path);
    } catch {
      // Keep the raw path.
    }
    return `${host}|${path.replace(/\/+$/, "").toLowerCase() || "/"}`;
  } catch {
    return null;
  }
}

/** Was this price read on the page `href` opens? Its source URL decides; an ASIN alone, for an Amazon item. */
function priceReadOn(
  source: { url?: string; asin?: string } | undefined,
  href: string,
  productAsin: string | undefined,
): boolean {
  if (!source) return false;
  const link = pageKey(href);
  if (!link) return false;
  if (source.url) return pageKey(source.url) === link;
  if (source.asin) {
    const asin = amazonAsinOf(href) ?? productAsin?.toUpperCase() ?? null;
    return !!asin && asin === source.asin.toUpperCase();
  }
  return false;
}

/**
 * A price shows only with its source (see the header). Returns the product
 * without its price when the price was not read on the page its buy link
 * opens, else the product itself:
 *  - an Amazon listing keeps a price with no source (prices written before
 *    2026-09-28 came from that listing) or one read on that listing;
 *  - a shop link keeps only a price read on that very page. With none, the
 *    offers engine's row for that shop may still price it (withOwnLinkPrice,
 *    which runs next);
 *  - an Amazon search, or no link at all, keeps none.
 */
export function withSourcedLinkPrice<T extends Product>(product: T): T {
  try {
    const price = product.price;
    const value = [price?.listingPrice, price?.lowestPrice].find((n) => typeof n === "number" && n > 0);
    if (value === undefined) return product;
    const link = product.externalLinks as ExternalLinks | undefined;
    const href = link?.href ?? "";
    const source = price?.source;
    const sourced = !!(source?.url || source?.asin);
    const search = !href || link?.type === "amazon-search" || isAmazonSearchHref(href);
    if (!search && isAmazonItemLink(link)) {
      if (!sourced || priceReadOn(source, href, product.ASIN)) return product;
    } else if (!search) {
      if (sourced && priceReadOn(source, href, undefined)) return product;
    }
    const out = { ...product } as T;
    delete (out as { price?: unknown }).price;
    return out;
  } catch {
    return product;
  }
}

/** A price must clear this confidence to stand as the product's price (the panel's own floor). */
const OWN_LINK_PRICE_MIN_CONFIDENCE = 0.5;

/**
 * A product whose buy link is a shop but which carries no price of its own
 * shows the price the offers engine read on that very page (its row for the
 * same shop, confident, in stock, in the market's currency). /find-product
 * chooses such links and leaves the price to the offers engine; this is where
 * that price reaches the card, the price line and the structured data.
 *
 * A shop that states a range (Apple: every storage size of one phone) gives
 * `observedPriceRange` instead, and no price: the low end is not what the
 * product costs. A row read on the very page the button opens needs no
 * identity verdict to stand: /find-product already chose that page for this
 * product. Returns the product itself when there is nothing to fill.
 */
export function withOwnLinkPrice<T extends Product>(product: T, regionCode: string | null | undefined): T {
  try {
    const listing = product.price?.listingPrice;
    if (typeof listing === "number" && listing > 0) return product;
    const link = product.externalLinks as ExternalLinks | undefined;
    if (!link || link.type !== "other" || !link.href || !Array.isArray(product.offers)) return product;
    const shop = normalizeMerchantDomain(hostOf(link.href) ?? "");
    if (!shop) return product;
    const region = REGION_FOR_CODE[(regionCode ?? "").toUpperCase() as RegionCode];
    const currency = region ? getCurrencyForRegion(region) : null;
    const href = link.href;
    const row = product.offers.find((offer) => {
      if (normalizeMerchantDomain(offer?.merchantDomain ?? "") !== shop) return false;
      if (offer.kind === "info" || offer.availability === "out_of_stock") return false;
      if (offer.priceKind !== undefined && offer.priceKind !== "sale") return false;
      const priced = typeof offer.price?.value === "number" && offer.price.value > 0;
      const stated = priced ? offer.price!.currency : offer.priceRange?.currency;
      if (!priced && !offer.priceRange) return false;
      if (currency && stated !== currency) return false;
      const readOnButtonPage = !!offer.verifiedAt && priceReadOn({ url: offer.url }, href, undefined);
      return (
        readOnButtonPage ||
        resolveOfferConfidence({ confidence: offer.confidence ?? undefined, source: offer.source ?? "find-offers" }) >=
          OWN_LINK_PRICE_MIN_CONFIDENCE
      );
    });
    if (!row) return product;
    if (row.price && row.price.value > 0) {
      // Sourced on the row that read it, so every reader can tell where it came from.
      return {
        ...product,
        price: {
          ...(product.price ?? {}),
          listingPrice: row.price.value,
          lowestPrice: row.price.value,
          source: { url: row.url },
        },
      };
    }
    if (!row.priceRange) return product;
    return {
      ...product,
      observedPriceRange: { ...row.priceRange, sourceUrl: row.url },
    };
  } catch {
    return product;
  }
}

/**
 * How long anything read from Amazon may be shown: the Associates IP License
 * (2(c)(h)) lets non-image Product Advertising Content be cached "for up to
 * 24 hours", after which it must be refreshed before it is shown again. The
 * offers read path keeps a Creators row exactly this long (read-window.ts).
 */
export const AMAZON_CONTENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function parseIsoMs(value: unknown): number | null {
  if (typeof value !== "string" || !value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** Inside the licence's window at `now`; with no `now` (the browser, after the server's check), any parseable time. */
function withinAmazonWindow(readMs: number | null, now: number | undefined): boolean {
  return readMs !== null && (now === undefined || now - readMs <= AMAZON_CONTENT_MAX_AGE_MS);
}

type OfferRow = NonNullable<Product["offers"]>[number];

/** An Amazon price this page may show: read through the Creators API, inside the window. */
export function isCurrentAmazonRow(offer: OfferRow | undefined, now: number | undefined): boolean {
  if (!offer || offer.source !== "creators-api") return false;
  return withinAmazonWindow(parseIsoMs(offer.observedAt) ?? parseIsoMs(offer.verifiedAt), now);
}

/**
 * The Amazon listing price a page may show, and nothing older (Olivier,
 * 2026-10-03: "accurate, up to date and compliant", no added spend):
 *
 *  - Amazon allows a price only when it came through the Creators API
 *    (Participation Requirements 2) and was read within 24 hours (above). An
 *    Amazon row from any other source, or one read longer ago, leaves the
 *    product's offers;
 *  - an Amazon listing shows the price of this market's current Creators
 *    row for it, never the price stored on the product, which the visit path
 *    wrote at its last refresh and can be weeks old (the cron refreshes the
 *    rows every 6 to 24 hours at no extra cost). No current row, or a row
 *    saying the listing is out of stock: no price, and the card keeps its
 *    link. The kept row's `observedAt` is the "as of" stamp every surface
 *    prints beside that price (listingPriceObservedAt finds it by store and
 *    amount).
 *
 * `now` is given on the server. The browser re-runs this on live snapshots
 * without it: the rows it holds already passed the server's check, and a
 * second clock would only make the hydrated card disagree with the cached HTML.
 */
export function withCurrentAmazonPrice<T extends Product>(
  product: T,
  regionCode: string | null | undefined,
  now?: number,
): T {
  try {
    let out = product;
    if (Array.isArray(product.offers)) {
      const kept = product.offers.filter(
        (offer) =>
          !(isAmazonHost(offer?.merchantDomain) || offer?.source === "creators-api") || isCurrentAmazonRow(offer, now),
      );
      if (kept.length !== product.offers.length) {
        out = { ...out } as T;
        if (kept.length > 0) out.offers = kept;
        else delete (out as { offers?: unknown }).offers;
      }
    }

    const link = product.externalLinks as ExternalLinks | undefined;
    if (!isAmazonItemLink(link) || isAmazonSearchHref(link?.href ?? "")) return out;
    const currency = marketCurrency(regionCode);
    const row = (out.offers ?? []).find(
      (offer) =>
        isCurrentAmazonRow(offer, now) &&
        isAmazonHost(offer.merchantDomain) &&
        offer.kind !== "info" &&
        (offer.priceKind === undefined || offer.priceKind === "sale" || offer.priceKind === "regular") &&
        offer.availability !== "out_of_stock" &&
        typeof offer.price?.value === "number" &&
        offer.price.value > 0 &&
        (!currency || offer.price.currency === currency) &&
        resolveOfferConfidence({ confidence: offer.confidence ?? undefined, source: offer.source }) >=
          OWN_LINK_PRICE_MIN_CONFIDENCE,
    );
    if (!row?.price) {
      if (!product.price) return out;
      const priceless = { ...out } as T;
      delete (priceless as { price?: unknown }).price;
      return priceless;
    }
    const value = row.price.value;
    const was = row.originalPrice?.value;
    const saving =
      typeof was === "number" && was > value
        ? { amount: Math.round((was - value) * 100) / 100, percentage: Math.round(((was - value) / was) * 100) }
        : undefined;
    // Only what the read says: its price, and its saving against Amazon's own
    // was-price. A stored range end, unit price or price history is the
    // stored listing's, not this read's, and leaves with it (the JSON-LD
    // prints nothing the card does not show). A source the product carried
    // (withSourcedLinkPrice kept it: it names this listing) stays.
    const price = {
      listingPrice: value,
      lowestPrice: value,
      ...(saving ? { saving } : {}),
      ...(product.price?.source ? { source: product.price.source } : {}),
    };
    const unchanged =
      !!product.price &&
      Object.keys(product.price).every((key) => key in price) &&
      product.price.listingPrice === value &&
      (product.price.lowestPrice === undefined || product.price.lowestPrice === value) &&
      JSON.stringify(product.price.saving ?? null) === JSON.stringify(saving ?? null);
    if (unchanged) return out;
    return { ...out, price };
  } catch {
    return product;
  }
}

/* ------------------------------------------------------------------ *
 * The one price a card shows
 * ------------------------------------------------------------------ */

export type ShownPriceKind = "listing" | "pick" | "sale";

export interface ShownPriceSeller {
  /** "Amazon", or the store's display name ("Best Buy", "Apple"). */
  name: string;
  /** The store's host, lower-cased, without "www." (amazon.ca, bestbuy.ca): the `data-price-source` value. */
  domain: string;
  /** The store's bare origin: an identity claim ("this price is Best Buy's"), never a buy link. */
  url: string;
  isAmazon: boolean;
}

/**
 * The one priced row a card shows, for the price line under its button, the
 * JSON-LD, the search shelf and a kit line alike (Search QA 2026-10-19, item
 * 2(3)): before this each surface chose a row of its own, and the JSON-LD
 * printed a used-car listing at 33,995 under a card whose button opened a
 * review site and whose line showed an estimate.
 */
export interface ShownPrice {
  amount: number;
  /** ISO 4217. */
  currency: string;
  /**
   * When the price was read (ISO-8601), or null when no observation vouches
   * for it. Never null for an Amazon price: Amazon's price shows only with
   * the time its API gave it (Associates IP License 2(c)(i)), and only from a
   * current Creators row (withCurrentAmazonPrice).
   */
  observedAt: string | null;
  /** The was-price when the row shows a saving the page may claim today. */
  wasAmount?: number;
  seller: ShownPriceSeller;
  /**
   * 'listing': the price of the card's own link (the product's `price`, as
   * presentProduct left it). 'pick': the price of the store the card's panel
   * recommends, what its button opens. 'sale': a clinic product's one real
   * sale at a store in this market.
   */
  kind: ShownPriceKind;
  /** The offers row the price came from, when one did. */
  row?: ShownPickRow;
}

/**
 * The offer the card's button opens when the card compares stores (its
 * panel's pick), as the panel prepared it. `originalPriceLabel` is the
 * panel's own was-price verdict: '' means it withheld the was-price (against
 * the store's typical price), and this rule withholds it too.
 */
export type ShownPickRow = Omit<OfferRow, "kind"> & { kind?: string; originalPriceLabel?: string };

export interface ShownPriceOptions {
  /**
   * The card's pick: the offer its button opens when it compares two or more
   * stores, or a clinic product's one sale. `null` when the card has decided
   * it has none (its button is its own link); undefined when the caller is
   * not a card (the search shelf's projection, an AI result), which reads the
   * card's own link alone.
   */
  pick?: ShownPickRow | null;
  /** Which kind the pick is; 'pick' when absent. */
  pickKind?: "pick" | "sale";
}

function toCents(amount: number): number {
  return Math.round(amount * 100);
}

/** When a row's price was observed: its source's own time, else the success clock; null when neither parses. */
function rowObservedAt(row: { observedAt?: string; verifiedAt?: string } | null | undefined): string | null {
  if (!row) return null;
  if (parseIsoMs(row.observedAt) !== null) return row.observedAt as string;
  if (parseIsoMs(row.verifiedAt) !== null) return row.verifiedAt as string;
  return null;
}

/**
 * The one price the card shows, or null when it shows none.
 *
 * The rule, in the order the card applies it:
 *  1. the pick's price when the card has a pick (`options.pick`): a priced
 *     offer row, never an information or search row. An Amazon pick without
 *     the time of its read shows no price, and the rule falls to 2;
 *  2. the price of the card's own link: the product's `price` (which
 *     presentProduct has already held to its source: read on that link's
 *     page, or Amazon's current Creators row), beside a listing or a store
 *     page, never beside an Amazon search. An Amazon listing's price shows
 *     only with the current Creators row that carries that very amount,
 *     whose read time is the price's; a store's price shows with the read
 *     time of the store's row when one carries the amount, and without one
 *     otherwise. The was-price (`price.saving`) is claimed only within a day
 *     of that read: a saving read long ago is no deal we can vouch for.
 *
 * `now` is the server's clock; the browser re-runs this on a live snapshot
 * without it (the rows it holds already passed the server's check). Pure;
 * never throws.
 */
export function resolveShownPrice(
  product: Product | null | undefined,
  regionCode: string | null | undefined,
  now?: number,
  options?: ShownPriceOptions,
): ShownPrice | null {
  try {
    if (!product) return null;
    const currency = marketCurrency(regionCode);
    const pick = options?.pick;
    if (pick) {
      const fromPick = pickShownPrice(pick, currency, options?.pickKind ?? "pick");
      if (fromPick) return fromPick;
    }
    return listingShownPrice(product, regionCode, currency, now);
  } catch {
    return null;
  }
}

function pickShownPrice(pick: ShownPickRow, currency: string | null, kind: "pick" | "sale"): ShownPrice | null {
  if (pick.kind === "info" || pick.kind === "search") return null;
  const amount = pick.price?.value;
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) return null;
  const domain = normalizeMerchantDomain(pick.merchantDomain ?? "");
  if (!domain) return null;
  const isAmazon = isAmazonHost(domain);
  const observedAt = rowObservedAt(pick);
  // An Amazon price shows only with the time Amazon's API gave it.
  if (isAmazon && !observedAt) return null;
  const was = pick.originalPrice?.value;
  const wasAmount =
    typeof was === "number" &&
    Number.isFinite(was) &&
    was > amount &&
    (pick.originalPrice?.currency ?? pick.price?.currency) === pick.price?.currency &&
    pick.originalPriceLabel !== ""
      ? was
      : undefined;
  return {
    amount,
    currency: pick.price?.currency ?? currency ?? "USD",
    observedAt,
    ...(wasAmount !== undefined ? { wasAmount } : {}),
    seller: {
      name: isAmazon ? "Amazon" : pick.merchantName?.trim() || lookupOfferMerchant(domain)?.name || domain,
      domain,
      url: `https://${domain}`,
      isAmazon,
    },
    kind,
    row: pick,
  };
}

function listingShownPrice(
  product: Product,
  regionCode: string | null | undefined,
  currency: string | null,
  now: number | undefined,
): ShownPrice | null {
  const link = product.externalLinks as ExternalLinks | undefined;
  const href = link?.href;
  if (!href || link?.type === "amazon-search" || isAmazonSearchHref(href)) return null;
  const amount = [product.price?.listingPrice, product.price?.lowestPrice].find(
    (n) => typeof n === "number" && Number.isFinite(n) && n > 0,
  );
  if (amount === undefined) return null;
  const host = hostOf(href);
  if (!host) return null;
  const isAmazon = isAmazonItemLink(link);
  const store = normalizeMerchantDomain(host);
  const row = (product.offers ?? []).find(
    (offer) =>
      !!offer &&
      offer.kind !== "info" &&
      normalizeMerchantDomain(offer.merchantDomain ?? "") === store &&
      typeof offer.price?.value === "number" &&
      toCents(offer.price.value) === toCents(amount) &&
      (!isAmazon || isCurrentAmazonRow(offer, now)),
  );
  const observedAt = row ? rowObservedAt(row) : null;
  if (isAmazon && !observedAt) return null;
  // A saving is a claim about today: only within a day of the read it came with.
  const saving = product.price?.saving?.amount;
  const observedMs = observedAt ? Date.parse(observedAt) : Number.NaN;
  const wasAmount =
    typeof saving === "number" &&
    Number.isFinite(saving) &&
    saving > 0 &&
    Number.isFinite(observedMs) &&
    (now ?? Date.now()) - observedMs <= AMAZON_CONTENT_MAX_AGE_MS
      ? Math.round((amount + saving) * 100) / 100
      : undefined;
  const region = REGION_FOR_CODE[(regionCode ?? "").toUpperCase() as RegionCode];
  const domain = isAmazon && region ? getAmazonDomainForRegion((regionCode ?? "").toUpperCase() as RegionCode) : store;
  return {
    amount,
    currency: currency ?? "USD",
    observedAt,
    ...(wasAmount !== undefined ? { wasAmount } : {}),
    seller: {
      name: isAmazon ? "Amazon" : getRetailerName(href) ?? getDisplayDomain(href, product),
      domain,
      url: isAmazon ? `https://www.${domain}` : `https://${domain}`,
      isAmazon,
    },
    kind: "listing",
    ...(row ? { row } : {}),
  };
}

/**
 * Is this Amazon star rating one the page may show? It came through the
 * Creators API (ratingSource 'amazon-asin', the only way Amazon allows its
 * ratings to be shown: Participation Requirements 6(t)) and was read within
 * the licence's 24 hours (`ratingCheckedAt`). Ratings stored before
 * 2026-10-03 carry no read time and never show again; one shows only after a
 * refresh that returns it, and the Creators API omits ratings for our
 * credentials today (creators-catalog-resources.ts).
 */
export function isCurrentAmazonRating(
  product: Pick<Product, "ratingSource" | "ratingCheckedAt"> | null | undefined,
  now?: number,
): boolean {
  return product?.ratingSource === "amazon-asin" && withinAmazonWindow(parseIsoMs(product.ratingCheckedAt), now);
}

/** A rating the page may not show is marked unverified, which every star, count and schema reader already refuses. */
export function withCurrentAmazonRating<T extends Product>(product: T, now?: number): T {
  if (product?.ratingSource !== "amazon-asin" || isCurrentAmazonRating(product, now)) return product;
  return { ...product, ratingSource: "unverified" };
}

function withoutWrongAmazon<T extends Product>(product: T, regionCode: string | null | undefined): T {
  try {
    const out = { ...product } as T;
    delete (out as { ASIN?: string }).ASIN;

    const link = product.externalLinks as ExternalLinks | undefined;
    const linkHost = hostOf(link?.href);
    if (link && isAmazonItemLink(link)) {
      const href =
        amazonSearchHref(product.title ?? "", regionCode, isAmazonHost(linkHost) ? linkHost : null) ??
        link.amazonSearchFallback ??
        "";
      out.externalLinks = { href, alt: product.title ?? link.alt ?? "", type: "amazon-search" };
      // The price came with that listing.
      delete (out as { price?: unknown }).price;
    }

    // The brand came with that listing when the product's own name does not
    // carry it: an accessory seller's name under a hearing aid.
    const brandShown = ownBrand(product);
    if (brandShown !== product.brandName) out.brandName = brandShown;

    if (product.ratingSource === "amazon-asin") out.ratingSource = "unverified";

    if (Array.isArray(product.images)) {
      const kept = product.images.filter((img) => img?.override === true || !isAmazonImageHost(hostOf(img?.src)));
      if (kept.length !== product.images.length) out.images = kept;
    }

    if (Array.isArray(product.offers)) {
      const kept = product.offers.filter((offer) => !isAmazonHost(offer?.merchantDomain) && !isAmazonHost(hostOf(offer?.url)));
      if (kept.length !== product.offers.length) {
        if (kept.length > 0) out.offers = kept;
        else delete (out as { offers?: unknown }).offers;
      }
    }
    return out;
  } catch {
    return product;
  }
}

/** Lower-case words, accents removed: "TP-Link Archer" \u2192 ["tp", "link", "archer"]. */
function nameWords(text: string): string[] {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Does the product's own name carry this brand? Whole words only, so "LG" is
 * not found in "Algae". The whole brand as consecutive words, however it is
 * spaced or hyphenated ("TP-Link" in "TPLink Archer", "Arc'teryx" in
 * "Arcteryx Beta"), or its first word past an article when that word has
 * three letters or more ("Shiseido Ginza Tokyo" on "Shiseido Benefiance").
 */
function nameCarriesBrand(title: string, brand: string): boolean {
  const brandWords = nameWords(brand);
  const titleWords = nameWords(title);
  if (brandWords.length === 0 || titleWords.length === 0) return false;
  const whole = brandWords.join("");
  for (let start = 0; start < titleWords.length; start++) {
    let joined = "";
    for (let end = start; end < titleWords.length && joined.length < whole.length; end++) {
      joined += titleWords[end];
      if (joined === whole) return true;
    }
  }
  const lead = brandWords.find((word) => !BRAND_ARTICLES.has(word));
  return !!lead && lead.length >= 3 && titleWords.includes(lead);
}

/** Articles a brand may start with ("The North Face", "La Roche-Posay"). */
const BRAND_ARTICLES = new Set(["the", "a", "an", "le", "la", "les", "l", "el", "los", "las", "der", "die", "das"]);

/**
 * The brand a card whose Amazon listing was rejected may show: its brandName
 * when the product's own name carries it (or there is no name to check
 * against), and otherwise none. That brandName most likely came with the
 * rejected listing ("Otto Tech", an accessory seller, under a Phonak hearing
 * aid), and the name's first word is no brand either: "Soy Gel" is Franmar's,
 * "Sierra" was an Airlink card (review of 2026-09-25). The name itself, which
 * the card shows above, still carries any brand it has.
 */
function ownBrand(product: Product): string {
  const brand = product.brandName ?? "";
  const title = product.title ?? "";
  if (!brand.trim() || !title.trim() || nameCarriesBrand(title, brand)) return product.brandName;
  return "";
}
